/**
 * One place that decides whether the graph moves.
 *
 * Wraps framer-motion's `useReducedMotion()` so that (a) tests and the fixture switcher
 * can force either mode deterministically, and (b) there is exactly one answer in the
 * tree rather than a dozen components each asking the media query and disagreeing during
 * a transition.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useReducedMotion } from 'framer-motion';

/** `null` means "follow the operating system", which is the default everywhere. */
const MotionOverrideContext = createContext<boolean | null>(null);

export function MotionPreference({
  reduced,
  children,
}: {
  reduced: boolean | null;
  children: ReactNode;
}): React.JSX.Element {
  return (
    <MotionOverrideContext.Provider value={reduced}>{children}</MotionOverrideContext.Provider>
  );
}

/**
 * True when the graph should cross-fade instead of moving.
 *
 * Reduced motion is not "no feedback" — the selection still reads as a change, it just
 * changes opacity rather than travelling across the screen.
 */
export function useCalmMotion(): boolean {
  const override = useContext(MotionOverrideContext);
  const fromSystem = useReducedMotion();
  return override ?? fromSystem ?? false;
}
